<template>
    <div>
        <Loader :loader="isLoading" v-if="isLoading" />
        <div v-else>
            <div class="text-right mb-1">
                <b-button size="sm" type="button" variant="primary" @click="onClickNewGeneralComment">{{
                    $t('generalComment.newComment')
                }}</b-button>
            </div>
            <div v-if="comments.length" class="scroll">
                <b-media vertical-align="center" v-for="comment in comments" :key="comment.id" class="stripe pb-2">
                    <b-link :to="`/v2/profile/${comment.userId}`" slot="aside">
                        <avatar
                            :size="42"
                            :src="comment.userPicture"
                            :username="comment.userName"
                            class="hidden-sm-down mb-3"
                        />
                    </b-link>
                    <div class="d-flex justify-content-between">
                        <div class="mr-3">
                            <small class="text-muted">{{ comment.userName }}</small>

                            <small class="text-muted ml-1">{{ formatDate(comment) }}</small>

                            <b-badge class="ml-3" v-if="comment.type == 'n_box'" variant="primary">{{
                                comment.n_box.title
                            }}</b-badge>

                            <div v-html="comment.comment" class="comment-content text-small-sm-down" />
                        </div>

                        <div slot="aside">
                            <b-dropdown
                                size="sm"
                                variant="link"
                                :no-caret="true"
                                :dropleft="true"
                                class="ml-1"
                                v-if="comment.userId == currentUser.id"
                            >
                                <template slot="button-content">
                                    <i class="fa fa-ellipsis-v fa-lg"></i>
                                </template>
                                <b-dropdown-item
                                    v-if="comment.type !== 'n_box'"
                                    @click="onClickEditComment(comment.id)"
                                    >{{ $t('form.button.edit') }}</b-dropdown-item
                                >
                                <b-dropdown-item @click="deleteGeneralComment(comment)">{{
                                    $t('form.button.delete')
                                }}</b-dropdown-item>
                            </b-dropdown>
                        </div>
                    </div>
                </b-media>
            </div>
            <b-alert show variant="info" v-show="!comments.length && !isLoading">{{
                $t('profile.generalCommentTable.empty')
            }}</b-alert>
        </div>
    </div>
</template>

<script>
import i18n from '@/i18n'
import Avatar from 'vue-avatar'
import { validationMixin } from 'vuelidate'
import Loader from '@/views/components/loader'
import { Comment } from '@/views/components/fields'
import { success, danger } from '@/lib/notification'
import ButtonLoader from '@/views/components/button-loader'
import { getStringFromDateWithoutTime, getFromNow } from '@/lib/date'
import { proceedWithConfirmation } from '@/lib/sweet-alert'
import { getGeneralComments, getUserNBoxComments, deleteNBoxComment, deleteGeneralComment } from './api'

export default {
    name: 'GeneralComment',
    components: {
        Loader,
        Avatar,
        ButtonLoader,
        Comment,
    },
    mixins: [validationMixin],
    props: {
        user: {
            type: Object,
            required: true,
        },
        periodIds: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            comments: [],
            isLoading: true,
        }
    },
    mounted() {
        this.getComments()
    },
    methods: {
        onClickNewGeneralComment() {
            return this.$router.push(`/v2/general-comment/new?userId=${this.user.id}`)
        },
        onClickEditComment(id) {
            return this.$router.push(`/v2/general-comment/edit/${id}`)
        },
        getComments() {
            this.isLoading = true

            const nBoxCommentsPromise = getUserNBoxComments(this.user.id, this.periodIds)
            const generalCommentsPromise = getGeneralComments(this.currentUser.id, this.user.id, this.periodIds)

            Promise.all([nBoxCommentsPromise, generalCommentsPromise])
                .then((results) => {
                    let [nBoxComments, generalComments] = results
                    nBoxComments = this.mapperNBoxCommentsToList(nBoxComments)
                    this.setComments(nBoxComments, generalComments)
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        setComments(nBoxComments, generalComments) {
            this.comments = [...nBoxComments, ...generalComments].sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
            )
        },
        mapperNBoxCommentsToList(comments) {
            return comments.map((c) => {
                return {
                    ...c,
                    type: 'n_box',
                    comment: c.content,
                    userId: c.valuer_id,
                    userName: c.valuer.name,
                    userPicture: c.valuer.picture,
                }
            })
        },
        formatDate(obj) {
            return `${getStringFromDateWithoutTime(obj.created_at, 'YYYY-MM-DD')} - ${getFromNow(obj.created_at)}`
        },
        deleteGeneralComment(comment) {
            proceedWithConfirmation({
                onConfirm: () => {
                    if (comment.type == 'n_box') {
                        return deleteNBoxComment(comment.id)
                    }
                    return deleteGeneralComment(comment.id)
                },
                callback: () => {
                    this.notificationCommentDeletedSuccessfully()
                    return this.getComments()
                },
                onError: this.notificationError,
            })
        },
        notificationCommentDeletedSuccessfully() {
            success(
                i18n.t('notification.deletedSuccessfully', {
                    item: i18n.t('generalComment.comment'),
                })
            )
        },
        notificationError() {
            danger()
        },
    },
    computed: {
        currentUser() {
            return this.$store.state.currentUser.user
        },
    },
    watch: {
        'user.id'(newUserID, oldUserID) {
            if (newUserID != oldUserID) {
                this.getComments()
            }
        },
        periodIds(oldValue, newValue) {
            if (oldValue.length != newValue.length) {
                this.getComments()
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.scroll {
    max-height: 500px;
    padding-top: 15px;
    overflow-y: auto;
}

.stripe {
    position: relative;
    margin-bottom: 10px;
}

.stripe::before {
    position: absolute;
    bottom: 0;
    border-bottom: 1px solid #e4e4e4;
    width: 100%;
    left: 0%;
    content: '';
}

::v-deep .comment-content {
    text-align: justify;
    margin-top: 0.25rem;

    * {
        margin: 0;
    }
}
</style>
